// src/pages/ClientInvoices.js — exported as <SupplierInvoiceView>
// One settlement invoice, mounted at BOTH /supplier/invoices/:invoiceId and
// /owner/invoices/:invoiceId.
//
// The card was `background: '#fff'` and set no text colour, so everything in it
// inherited the layout's cream (#F4F1EA) — white text on a white card. Both
// layouts it renders under are the ink ground, so it was unreadable in the
// supplier dashboard AND the owner one; only the hard-coded blue headings and
// grey labels showed at all. Rebuilt on the shared chrome/ink primitives so the
// panel and the type come from the same palette as every other page.

import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api/baseURL';
import PayWithWaveButton from '../components/PayWithWaveButton';
import {
  Page, Panel, Table, Tr, Td, Badge, Button, Loading, ErrorNote, Note, BackLink,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const money = (n) => `${fmt(n)} FCFA`;

const R = ({ children }) => <span className="block text-right">{children}</span>;

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-[3px]">
      <span className="text-[11.5px] text-[#8F877B] shrink-0">{label}</span>
      <span className="text-[12.5px] text-[#F4F1EA] text-right min-w-0 break-words">{value}</span>
    </div>
  );
}

export default function SupplierInvoiceView({ authToken }) {
  const { invoiceId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const token = authToken || localStorage.getItem('token');

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // The same component serves the supplier and the owner dashboards, so the
  // way back is whichever list the reader came from — it used to always send
  // an owner to the supplier's invoice list.
  const backTo = pathname.startsWith('/owner') ? '/owner/invoices' : '/supplier/invoices';

  const isLikelyObjectId = (v) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);

  useEffect(() => {
    let on = true;
    (async () => {
      if (!invoiceId) { setErr('Identifiant de facture manquant'); setLoading(false); return; }
      if (!isLikelyObjectId(invoiceId)) { setErr('Identifiant de facture invalide'); setLoading(false); return; }

      setLoading(true);
      setErr('');
      try {
        const res = await axios.get(`${API_URL}/quotations/supplier-invoices/${invoiceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (on) setInv(res.data);
      } catch (e) {
        if (on) setErr(e?.response?.data?.error || e.message);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [invoiceId, token]);

  if (loading) {
    return <Page title="Facture"><Loading /></Page>;
  }
  if (err) {
    return (
      <Page title="Facture" actions={<BackLink onClick={() => navigate(backTo)} />}>
        <ErrorNote>{err}</ErrorNote>
      </Page>
    );
  }
  if (!inv) {
    return (
      <Page title="Facture" actions={<BackLink onClick={() => navigate(backTo)} />}>
        <ErrorNote>Facture introuvable.</ErrorNote>
      </Page>
    );
  }

  const supplier = inv?.sellerSupplier || {};
  const buyer = inv?.buyer || {};
  const vehicle = inv?.vehicle
    ? `${inv.vehicle.brand || ''} ${inv.vehicle.model || ''} ${inv.vehicle.year || ''}${inv.vehicle.vin ? ' — ' + inv.vehicle.vin : ''}`.trim()
    : '—';

  const totals = inv?.totals || {};
  const subtotal = Number(totals.clientSubtotal ?? totals.subtotalPublic ?? 0);
  const tax = Number(totals.taxPublic ?? 0);
  const total = Number(totals.clientTotal ?? totals.totalPublic ?? (subtotal + tax));
  const isPaid = inv?.status === 'paid';

  return (
    <Page
      subtitle="Facture fournisseur"
      title={`Facture ${String(inv._id).slice(-6).toUpperCase()}`}
      actions={
        <>
          <BackLink onClick={() => navigate(backTo)} />
          <Button onClick={() => window.print()}>Imprimer</Button>
        </>
      }
    >
      <div className="flex items-center gap-3">
        <Badge tone={isPaid ? 'ok' : 'warn'}>{isPaid ? 'Payée' : 'En attente de paiement'}</Badge>
        <span className="text-[11.5px] text-[#6E665C]">
          Créée le {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('fr-FR') : '—'}
        </span>
      </div>

      <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
        <Panel title="Fournisseur">
          <Row label="Nom" value={supplier.name || '—'} />
          <Row label="Entreprise" value={supplier.businessName || '—'} />
          <Row label="Email" value={supplier.email || '—'} />
          <Row label="Téléphone" value={supplier.phone || '—'} />
          <Row label="Adresse" value={supplier.location || supplier.address || '—'} />
        </Panel>

        <Panel title="Acheteur">
          <Row label="Nom" value={buyer.name || '—'} />
          <Row label="Entreprise" value={buyer.businessName || '—'} />
          <Row label="Email" value={buyer.email || '—'} />
          <Row label="Véhicule" value={vehicle} />
        </Panel>
      </div>

      <Panel title="Articles" bodyClass="p-0">
        <Table
          minWidth={760}
          head={[
            'Description',
            'Référence',
            'Marque',
            <R key="q">Quantité</R>,
            <R key="pu">Prix unitaire</R>,
            <R key="t">Total</R>,
          ]}
        >
          {(inv.items || []).map((it, idx) => (
            <Tr key={idx}>
              <Td className="text-[#F4F1EA]">{it.description || '—'}</Td>
              <Td mono>{it.reference || '—'}</Td>
              <Td>{it.brand || '—'}</Td>
              <Td mono className="text-right">{it.quantity || 0}</Td>
              <Td mono className="text-right">{money(it.publicUnitPrice)}</Td>
              <Td mono className="text-right">{money(it.lineTotalPublic)}</Td>
            </Tr>
          ))}
        </Table>
      </Panel>

      <div className="flex justify-end">
        <Panel className="w-full max-w-[340px]">
          <Row label="Sous-total" value={money(subtotal)} />
          {tax > 0 && <Row label="TVA 18%" value={money(tax)} />}
          <div className="mt-2 pt-2.5 border-t border-[#241F1B] flex items-baseline justify-between gap-4">
            <span className="text-[11px] uppercase tracking-[.16em] text-[#6E665C]">Total</span>
            <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
              {money(total)}
            </span>
          </div>
        </Panel>
      </div>

      {isPaid ? (
        <Note tone="ok">
          Paiement reçu. Net fournisseur : <b>{money(inv?.payment?.netToSupplier)}</b>.
        </Note>
      ) : (
        <Panel title="Paiement">
          <p className="text-[12.5px] text-[#C9C3B6] mb-3">
            Paiement mobile requis pour confirmer la réservation.
          </p>
          <PayWithWaveButton
            vehicleId={inv.vehicle?._id || inv.vehicle}
            amount={Number(total)}
            type="invoice"
            supplierId={
              inv.sellerSupplier && inv.sellerSupplier._id ? inv.sellerSupplier._id : inv.sellerSupplier
            }
            invoiceId={inv._id}
            onSuccess={() => window.location.reload()}
          />
        </Panel>
      )}

      <p className="text-[11px] text-[#5C554C]">
        ID : {inv._id}
        {inv.createdAt ? ` • Créée le ${new Date(inv.createdAt).toLocaleString('fr-FR')}` : ''}
      </p>
    </Page>
  );
}
